import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./style.scss";
import customStyles from "../../customStyles";
import { useDispatch, useSelector } from "react-redux";
import { getPfsPayoutList, requestPayouts } from "../../utils/apis/payments";
import { returnMessages } from "../../utils/store/reducers/Message";
import { createFormData } from "../../utils/helpers/string";
import Spinner from "../../reusableComponents/spinner/Spinner";
import { returnErrors } from "../../utils/store/reducers/Error";
import axios from "axios";

const TradingPayout = () => {
  const [selectedType, setSelectedType] = useState("standard");
  const [pfspayoutList, setPfsPayoutList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { idToken } = useSelector((state) => state.auth);

  const fetchPayout = async () => {
    setLoading(true);
    try {
      const response = await getPfsPayoutList(idToken);
      setPfsPayoutList(response);
    } catch (error) {
      console.error("Error fetching PFS list:", error);
      setError("Failed to fetch payout list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayout();
  }, []);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      login_id: null,
      amount: "",
      reason: "",
      payout_type: "PFS",
      method: "",
    },
    validationSchema: Yup.object({
      login_id: Yup.string().required("Account ID is required"),
      amount: Yup.number()
        .required("Amount is required")
        .positive("Amount must be positive"),
      method: Yup.string().required("Method is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = createFormData(values);
        const response = await requestPayouts(idToken, formData);
        if (response.status < 399) {
          dispatch(
            returnMessages(
              response.data.detail ||
                "Your payout has been successfully requested. You will be notified within 24 business hours.",
              200
            )
          );
        }
      } catch (errors) {
        const error = errors.response.data;
        let msg;
        if (Object.keys(error).length > 0) {
          const firstKey = Object.keys(error)[0];

          const firstString = error[firstKey] || error[firstKey][0];

          msg = firstString;
        } else {
          msg = "Something went wrong";
        }
        dispatch(returnErrors(msg, 400));

        console.error("Error requesting payouts:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const [preAmount, setPreAmount] = useState("");
  const [payableAmount, setPayableAmount] = useState(null);
  const debounceTimeout = useRef(null);

  const handlePayableAmount = (e) => {
    setPreAmount(e.target.value);
  };

  const fetchPayableAmount = async (amount) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      };
      if (!formik.values.login_id) {
        alert("Please first select your login ID");
        return;
      }
      const response = await axios.post(
        "user/payout/pyable-amount/",
        { amount: amount, login_id: formik.values.login_id },
        config
      );
      setPayableAmount(response.data);
    } catch (error) {
      console.error("Error fetching payable amount", error);
    }
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (preAmount) {
        fetchPayableAmount(preAmount);
      }
    }, 500);

    return () => {
      clearTimeout(debounceTimeout.current);
    };
  }, [preAmount]);

  return (
    <div className="trading_payout_container">
      <div className="header_section">
        <h2>Request a New Payout</h2>
        <p>
          Please use this form to request a new payment; we will check and
          contact you for additional information.
        </p>
      </div>
      <div className="note_section">
        Please note*: Accounts in the below dropdown will populate only on their
        respective withdrawal dates.
      </div>
      <div className="choose_account_section">
        <div className="type">
          <div
            className={`standard ${
              selectedType === "standard" ? "active" : ""
            }`}
            onClick={() => setSelectedType("standard")}
          >
            <p>Standard</p>
            <span>Request to withdraw the full amount payable</span>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <h3>Choose Account Login Id</h3>
          <label htmlFor="login_id" className="form_input_label">
            <Select
              className="form_input_box react-select-container"
              classNamePrefix="react-select"
              name="login_id"
              placeholder="Select your Account Id"
              styles={customStyles}
              id="login_id"
              isSearchable={true}
              isLoading={loading}
              options={
                pfspayoutList?.data?.map((obj) => ({
                  value: obj.account,
                  label: obj.account,
                  amount: obj.payable_amt,
                })) || []
              }
              onChange={(option) => {
                formik.setFieldValue("login_id", option.value);
                formik.setFieldValue("amount", "");
              }}
              onBlur={formik.handleBlur}
            />
            {formik.touched.login_id && formik.errors.login_id ? (
              <div className="error_message">{formik.errors.login_id}</div>
            ) : null}
          </label>
          <label htmlFor="method" className="form_input_label">
            Payment Method
            <Select
              className="form_input_box react-select-container"
              classNamePrefix="react-select"
              name="method"
              placeholder="Select your Payment Method"
              styles={customStyles}
              id="method"
              isSearchable={true}
              options={[
                { label: "ACH", value: "ACH" },
                { label: "WIRE", value: "WIRE" },
                { label: "SWIFT", value: "SWIFT" },
                { label: "WISE", value: "WISE" },
                { label: "RISE", value: "RISE" },
              ]}
              onChange={(option) =>
                formik.setFieldValue("method", option.value)
              }
              onBlur={formik.handleBlur}
            />
            {formik.touched.method && formik.errors.method ? (
              <div className="error_message">{formik.errors.method}</div>
            ) : null}
          </label>

          <label htmlFor="amount" className="form_input_label">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Withdraw Amount</span>
              {payableAmount?.payable_profit && (
                <span style={{ color: "#4bb390" }}>
                  Payable amount: {payableAmount?.payable_profit}
                </span>
              )}
            </div>
            <input
              type="text"
              className="form_input_box"
              id="amount"
              name="amount"
              placeholder="0"
              onChange={(e) => {
                formik.handleChange(e);
                handlePayableAmount(e);
              }}
              onBlur={formik.handleBlur}
              value={formik.values.amount}
            />
            {formik.touched.amount && formik.errors.amount ? (
              <div className="error_message">{formik.errors.amount}</div>
            ) : null}
          </label>

          <label htmlFor="reason" className="form_input_label">
            Reason
            <input
              type="text"
              className="form_input_box"
              id="reason"
              name="reason"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.reason}
            />
            {formik.touched.reason && formik.errors.reason ? (
              <div className="error_message">{formik.errors.reason}</div>
            ) : null}
          </label>
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="standard-button"
          >
            {formik.isSubmitting ? <Spinner size={20} stroke={2} /> : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TradingPayout;
